import { FC, useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AuthSessionContext } from '@context/AuthContext'

import { NavigationDownUpIcon } from '@icons/NavigationDownUpIcon'
import { ThreeDotIsVerticalIcon } from '@icons/ThreeDotIsVerticalIcon'

import { Tooltip } from '@ui/Popups/Tooltip/Tooltip'
import { Body1 } from '@ui/Typography'
import { Dropdown } from '@ui/v2/Dropdown/Dropdown'
import { DropdownCard } from '@ui/v2/Dropdown/DropdownCard'
import { DropdownList } from '@ui/v2/Dropdown/DropdownList'
import { DropdownListItem } from '@ui/v2/Dropdown/DropdownListItem'
import { EditIcon } from '@ui/v2/Icons/EditIcon'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadSort,
  TableHeader,
  TableNoResults,
  TableRow,
} from '@ui/v2/Table/Table'
import { TableHeaderTooltip } from '@ui/v2/Table/TableHeaderTooltip'

import { PageQuery } from '@utils/ApiClient'
import { formatDate } from '@utils/date.utils'

import { NameDto } from '@model/NameDto'
import {
  TradingAccountRebateDetailedDto,
  hasRebateSettings,
} from '@model/TradingAccountRebateDetailedDto'

import { Button } from '@global/button/Button'
import IconButton from '@global/iconButton/IconButton'

import { useFormatNumber } from '@hooks/useFormatNumber'

import { TradingAccountsRebateSortOptions } from './hooks/useIBTradingAccountsRebate'

import styles from './ClientsPageTable.module.scss'

interface ClientsPageTableProps {
  tableData: TradingAccountRebateDetailedDto[]
  sortOptions?: NameDto<TradingAccountsRebateSortOptions | null>[]
  pageQuery: PageQuery | undefined
  sticky?: 'left'
  selectedIds?: string[]
  onSelect?: (ids: string[]) => void
  setPageQuery?: (value: PageQuery) => void
  onEdit: (client: TradingAccountRebateDetailedDto) => void
  onReset: (client: TradingAccountRebateDetailedDto) => void
}

export const ClientsPageTable: FC<ClientsPageTableProps> = (props) => {
  const {
    tableData,
    sortOptions,
    pageQuery,
    sticky,
    selectedIds = [],
    setPageQuery,
    onEdit,
    onSelect,
    onReset,
  } = props

  const { formatNumber, formatMoney } = useFormatNumber()
  const { t } = useTranslation()
  const [auth] = useContext(AuthSessionContext)
  const dateFormat = auth?.dateFormatType?.name

  const handleSelect = (id: string, selected: boolean) => {
    if (selected) {
      onSelect?.(selectedIds?.filter((activeId) => activeId !== id))

      return
    }

    onSelect?.([...selectedIds, id])
  }

  return (
    <Table sticky={sticky} className={styles.table}>
      <TableHeader>
        <TableRow>
          {sortOptions?.map((option) => {
            return option.id ? (
              <TableHeadSort
                id={option.id}
                key={option.id}
                sort={pageQuery?.sort || undefined}
                sortOrder={pageQuery?.sortOrder}
                setSort={(sort, sortOrder) => {
                  setPageQuery?.({
                    ...pageQuery,
                    sort,
                    sortOrder,
                  })
                }}
              >
                {option.name}
                <TableHeaderTooltip<Partial<Record<TradingAccountsRebateSortOptions, string>>>
                  headerIds={{
                    TotalVolumeFxm: t('IB.RebatesPage.Forex & Metals'),
                    TotalVolumeOther: t('IB.RebatesPage.Other instrument groups'),
                    TotalCommission: t('IB.RebatesPage.Commission accrued for TA'),
                  }}
                  targetHeaderId={option.id}
                />
              </TableHeadSort>
            ) : (
              <TableHead key={option.name}>{option.name}</TableHead>
            )
          })}
          <TableHead />
        </TableRow>
      </TableHeader>
      <TableBody>
        {tableData.map((item) => {
          const selected = !!selectedIds?.includes(item.tradingAccount?.id)

          return (
            <TableRow key={item.tradingAccount?.id} selected={selected}>
              <TableCell
                width={160}
                selected={selected}
                onSelect={() => handleSelect(item.tradingAccount.id, selected)}
              >
                <span className={styles.tradingAccount}>{item.tradingAccount?.name}</span>
              </TableCell>
              <TableCell width={140}>{item.client?.name}</TableCell>
              <TableCell width={112}>{item.tradingAccountType?.name}</TableCell>
              <TableCell width={120}>
                {formatDate(item.tradingAccountSignedUpDate, {
                  formatType: dateFormat,
                  withTime: false,
                })}
              </TableCell>
              <TableCell width={120}>{item.country?.name}</TableCell>
              <TableCell width={105}>{item.rebateGroup?.name}</TableCell>
              <TableCell width={120}>
                <Tooltip.New
                  content={formatNumber(item.totalVolumeFxm)}
                  disabled={item.totalVolumeFxm < 100_000_000}
                >
                  {formatNumber(item.totalVolumeFxm, 2)}
                </Tooltip.New>
              </TableCell>
              <TableCell width={120}>
                <Tooltip.New
                  content={formatNumber(item.totalVolumeOther)}
                  disabled={item.totalVolumeOther < 100_000_000}
                >
                  {formatNumber(item.totalVolumeOther, 2)}
                </Tooltip.New>
              </TableCell>
              <TableCell width={120}>{formatMoney(item.totalCommission)}</TableCell>
              <TableCell>
                {!item.rebateAmountInPercentage ? t('N/A') : `${item.rebateAmountInPercentage}%`}
              </TableCell>
              <TableCell align='right' className={styles.rowActions}>
                <RowActions
                  client={item}
                  onEdit={() => onEdit(item)}
                  onReset={() => onReset(item)}
                />
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}

interface RowActionsProps {
  onEdit: () => void
  onReset: () => void
  client: TradingAccountRebateDetailedDto
}

export const RowActions: FC<RowActionsProps> = (props) => {
  const { client, onEdit, onReset } = props

  const { t } = useTranslation()

  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  const actions = [
    {
      label: t('Edit'),
      onClick: () => {
        onEdit()
        setIsDropdownOpen(false)
      },
      icon: EditIcon,
    },
  ]

  if (hasRebateSettings(client)) {
    actions.push({
      label: t('Reset'),
      onClick: () => {
        onReset()
        setIsDropdownOpen(false)
      },
      icon: NavigationDownUpIcon,
    })
  }

  return (
    <Dropdown
      direction='topRight'
      isOpen={isDropdownOpen}
      setIsOpen={setIsDropdownOpen}
      content={
        <DropdownCard>
          <DropdownList>
            {actions?.map((action) => {
              return (
                <DropdownListItem
                  key={action.label}
                  onClick={action.onClick}
                  startIcon={action.icon}
                >
                  {action.label}
                </DropdownListItem>
              )
            })}
          </DropdownList>
          <div className={styles.actionsFooter}>
            <Button.New
              appearance='secondary'
              onClick={() => setIsDropdownOpen(false)}
              size='L'
              fullWidth
            >
              {t('Cancel')}
            </Button.New>
          </div>
        </DropdownCard>
      }
    >
      <IconButton.New appearance='ghost'>
        <ThreeDotIsVerticalIcon />
      </IconButton.New>
    </Dropdown>
  )
}

interface EmptyClientsPageTableProps {
  isAnyFilterActive?: boolean
}

export const EmptyClientsPageTable: FC<EmptyClientsPageTableProps> = (props) => {
  const { isAnyFilterActive } = props

  const { t } = useTranslation()

  return (
    <TableNoResults
      title={
        isAnyFilterActive
          ? t('IB.RebatesPage.Nothing to show')
          : t('IB.RebatesPage.ClientsPage.No clients to display')
      }
      content={
        isAnyFilterActive ? <Body1>{t('IB.RebatesPage.Please, adjust your filters')}</Body1> : null
      }
    />
  )
}
